import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Modal,
  Select,
  Textarea,
  type SelectOption,
} from '../../ui';
import {
  blankChangeDraft,
  parseList,
  validateChangeDraft,
} from '../../features/changes/changeModel';
import type {
  EngineeringChange,
  EngineeringChangeDraft,
} from '../../features/changes/types';
import type { GoalSummary } from '../../features/goals/types';
import type { WorkstreamSummary } from '../../features/workstreams/types';

export interface ChangeModalProps {
  open: boolean;
  onClose: () => void;
  /** Default engineer alias (the current user). */
  defaultEngineer: string;
  /** Goals — populate the linked-goal picker. */
  goals: GoalSummary[];
  /** Workstreams — populate the linked-workstream picker. */
  workstreams: WorkstreamSummary[];
  onCreate: (draft: EngineeringChangeDraft) => Promise<EngineeringChange>;
}

/**
 * Log-a-change form (manual entry). The human supplies the engineering CONTEXT
 * a raw commit/deploy log lacks — the why and the impact — which is why both
 * are required. Multi-value fields accept comma or newline separated values.
 *
 * EXTENSION POINT (not available yet): auto-derivation of change entries from
 * sprint stories, deploys, and PRs would pre-fill "what changed" and the
 * related ids here, leaving the human to add/confirm the why + impact. That
 * ingestion is deferred (same posture as SpecStudio / code-Knowledge); until it
 * lands, changes are entered manually.
 */
export function ChangeModal({
  open,
  onClose,
  defaultEngineer,
  goals,
  workstreams,
  onCreate,
}: ChangeModalProps) {
  const [draft, setDraft] = useState<EngineeringChangeDraft>(
    blankChangeDraft(defaultEngineer),
  );
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  const [storiesText, setStoriesText] = useState('');
  const [docsText, setDocsText] = useState('');

  useEffect(() => {
    if (!open) return;
    const base = blankChangeDraft(defaultEngineer);
    setDraft(base);
    setProblems([]);
    setStoriesText('');
    setDocsText('');
  }, [open, defaultEngineer]);

  const goalOptions: SelectOption[] = goals.map((g) => ({
    value: g.id,
    label: g.title || g.id,
  }));
  const workstreamOptions: SelectOption[] = workstreams.map((w) => ({
    value: w.id,
    label: w.title || w.id,
  }));

  const set = <K extends keyof EngineeringChangeDraft>(
    key: K,
    value: EngineeringChangeDraft[K],
  ) => setDraft((d) => ({ ...d, [key]: value }));

  const buildDraft = (): EngineeringChangeDraft => ({
    ...draft,
    title: draft.title.trim(),
    engineer: draft.engineer.trim(),
    relatedStoryIds: parseList(storiesText),
    relatedDocIds: parseList(docsText),
    goalId: draft.goalId || undefined,
    workstreamId: draft.workstreamId || undefined,
  });

  const submit = async () => {
    const next = buildDraft();
    const found = validateChangeDraft(next);
    if (found.length > 0) {
      setProblems(found);
      return;
    }
    setSaving(true);
    try {
      await onCreate(next);
      onClose();
    } catch (e) {
      setProblems([e instanceof Error ? e.message : 'Failed to log change.']);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => !saving && onClose()}
      size="lg"
      title="Log a change"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button loading={saving} onClick={submit}>
            Log change
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {problems.length > 0 && (
          <ul className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {problems.map((p) => (
              <li key={p}>• {p}</li>
            ))}
          </ul>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Title"
            placeholder="e.g. Switched checkout to async pricing calls"
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <Input
            label="Engineer (alias/name)"
            value={draft.engineer}
            onChange={(e) => set('engineer', e.target.value)}
          />
        </div>

        <Textarea
          label="Why (context — required)"
          hint="The engineering context a commit log doesn't capture."
          rows={2}
          value={draft.why}
          onChange={(e) => set('why', e.target.value)}
        />
        <Textarea
          label="Impact (required)"
          hint="Who / what is affected by this change."
          rows={2}
          value={draft.impact}
          onChange={(e) => set('impact', e.target.value)}
        />
        <Textarea
          label="What changed"
          rows={2}
          value={draft.changed}
          onChange={(e) => set('changed', e.target.value)}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Linked goal"
            placeholder="None"
            value={draft.goalId ?? ''}
            options={goalOptions}
            onChange={(e) => set('goalId', e.target.value || undefined)}
          />
          <Select
            label="Linked workstream"
            placeholder="None"
            value={draft.workstreamId ?? ''}
            options={workstreamOptions}
            onChange={(e) => set('workstreamId', e.target.value || undefined)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Related story ids — Task ids (comma separated)"
            value={storiesText}
            onChange={(e) => setStoriesText(e.target.value)}
          />
          <Input
            label="Related doc ids (comma separated)"
            value={docsText}
            onChange={(e) => setDocsText(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}

export default ChangeModal;
